import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { AdminPageHeader } from './AdminLayout';
import { Modal, Input, Check } from './AdminLangCountry';
import { Loader2, Plus, Trash2, Edit3 } from 'lucide-react';

export function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = () => Promise.all([api.get('/admin/users'), api.get('/admin/roles')]).then(([u, r]) => { setUsers(u.data || []); setRoles(r.data || []); });
  useEffect(() => { load(); }, []);

  const save = async (data) => {
    if (editing?.id) {
      const patch = { name: data.name, role_id: data.role_id, active: data.active };
      if (data.password) patch.password = data.password;
      await api.patch(`/admin/users/${editing.id}`, patch);
    } else {
      await api.post('/admin/users', data);
    }
    setShowForm(false); setEditing(null); load();
  };
  const remove = async (id) => { if (!window.confirm('Delete this user?')) return; await api.delete(`/admin/users/${id}`); load(); };

  const roleName = (id) => roles.find(r => r.id === id)?.name || '—';

  return (
    <div>
      <AdminPageHeader title="Users" description="Create operators that can access the console."
        actions={<button onClick={() => { setEditing({ email: '', name: '', password: '', role_id: roles[0]?.id, active: true }); setShowForm(true); }} className="btn-pill" disabled={!roles.length}><Plus size={16} /><span>Add user</span></button>} />
      <div className="rounded-3xl bg-white border border-black/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-xs uppercase tracking-widest text-stone-500"><tr><th className="text-left px-5 py-3">Name</th><th className="text-left px-5 py-3">Email</th><th className="text-left px-5 py-3">Role</th><th className="text-left px-5 py-3">Status</th><th></th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-black/5">
                <td className="px-5 py-3 font-medium">{u.name}</td>
                <td className="px-5 py-3">{u.email}</td>
                <td className="px-5 py-3">{roleName(u.role_id)}</td>
                <td className="px-5 py-3">{u.active ? <span className="text-green-700">Active</span> : <span className="text-stone-500">Disabled</span>}</td>
                <td className="px-5 py-3 text-right">
                  <button onClick={() => { setEditing({ ...u, password: '' }); setShowForm(true); }} className="p-2 hover:text-[#1E2DFF]"><Edit3 size={14} /></button>
                  <button onClick={() => remove(u.id)} className="p-2 hover:text-red-600"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showForm && <Modal onClose={() => { setShowForm(false); setEditing(null); }} title={editing?.id ? 'Edit user' : 'Add user'}>
        <UserForm initial={editing} roles={roles} onSubmit={save} />
      </Modal>}
    </div>
  );
}

function UserForm({ initial, roles, onSubmit }) {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  const sub = async (e) => { e.preventDefault(); setBusy(true); try { await onSubmit(f); } finally { setBusy(false); } };
  return (
    <form onSubmit={sub} className="space-y-3">
      <Input label="Name" value={f.name || ''} onChange={set('name')} required />
      {!initial?.id && <Input label="Email" type="email" value={f.email || ''} onChange={set('email')} required />}
      <Input label={initial?.id ? 'New password (leave blank to keep)' : 'Password'} type="password" value={f.password || ''} onChange={set('password')} required={!initial?.id} />
      <label className="block">
        <span className="text-sm font-medium text-stone-700">Role</span>
        <select value={f.role_id || ''} onChange={set('role_id')} required className="mt-1 w-full rounded-xl border border-black/15 bg-white px-3 py-2.5 text-sm">
          {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
      </label>
      <Check label="Active" checked={f.active} onChange={set('active')} />
      <button type="submit" disabled={busy} className="btn-pill">{busy ? <Loader2 className="animate-spin" size={16} /> : <span>Save</span>}</button>
    </form>
  );
}

export function AdminRoles() {
  const [roles, setRoles] = useState([]);
  const [perms, setPerms] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = () => Promise.all([api.get('/admin/roles'), api.get('/admin/permissions')])
    .then(([r, p]) => { setRoles(r.data || []); setPerms(p.data?.permissions || []); });
  useEffect(() => { load(); }, []);

  const save = async (data) => {
    if (editing?.id) await api.patch(`/admin/roles/${editing.id}`, { name: data.name, description: data.description, permissions: data.permissions });
    else await api.post('/admin/roles', data);
    setShowForm(false); setEditing(null); load();
  };
  const remove = async (id) => { if (!window.confirm('Delete this role?')) return; await api.delete(`/admin/roles/${id}`); load(); };

  return (
    <div>
      <AdminPageHeader title="Roles & Permissions" description="Define custom roles and grant fine-grained permissions."
        actions={<button onClick={() => { setEditing({ name: '', description: '', permissions: [] }); setShowForm(true); }} className="btn-pill"><Plus size={16} /><span>Add role</span></button>} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {roles.map((r) => (
          <div key={r.id} className="rounded-3xl bg-white border border-black/10 p-6">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="flex items-center gap-2">
                  <span className="heading-display text-2xl">{r.name}</span>
                  {r.system && <span className="text-xs bg-stone-200 px-2 py-0.5 rounded">system</span>}
                </p>
                <p className="text-sm text-stone-600 mt-1">{r.description || '—'}</p>
              </div>
              <div className="flex gap-1">
                <button onClick={() => { setEditing(r); setShowForm(true); }} className="p-2 hover:text-[#1E2DFF]"><Edit3 size={14} /></button>
                {!r.system && <button onClick={() => remove(r.id)} className="p-2 hover:text-red-600"><Trash2 size={14} /></button>}
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-1.5">
              {(r.permissions || []).map((p) => <span key={p} className="text-xs px-2 py-1 rounded-full bg-[#F4E94B] font-mono">{p}</span>)}
              {!r.permissions?.length && <span className="text-xs text-stone-400">No permissions</span>}
            </div>
          </div>
        ))}
      </div>
      {showForm && <Modal onClose={() => { setShowForm(false); setEditing(null); }} title={editing?.id ? 'Edit role' : 'Create role'}>
        <RoleForm initial={editing} permissions={perms} onSubmit={save} />
      </Modal>}
    </div>
  );
}

function RoleForm({ initial, permissions, onSubmit }) {
  const [f, setF] = useState({ ...initial });
  const [busy, setBusy] = useState(false);
  const isSystem = !!initial?.system;
  const togglePerm = (p) => {
    const cur = new Set(f.permissions || []);
    cur.has(p) ? cur.delete(p) : cur.add(p);
    setF({ ...f, permissions: Array.from(cur) });
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const sub = async (e) => { e.preventDefault(); setBusy(true); try { await onSubmit(f); } finally { setBusy(false); } };
  // Group permissions by prefix
  const groups = permissions.reduce((acc, p) => {
    const k = p.includes('.') ? p.split('.')[0] : 'core';
    (acc[k] = acc[k] || []).push(p); return acc;
  }, {});
  return (
    <form onSubmit={sub} className="space-y-3">
      <Input label="Name" value={f.name || ''} onChange={set('name')} required disabled={isSystem} />
      <Input label="Description" value={f.description || ''} onChange={set('description')} />
      <div>
        <p className="text-sm font-medium text-stone-700 mb-2">Permissions</p>
        <div className="max-h-[40vh] overflow-auto space-y-3 pr-1">
          {Object.entries(groups).map(([g, perms]) => (
            <div key={g} className="rounded-xl border border-black/10 p-3">
              <p className="text-xs uppercase tracking-widest font-semibold text-stone-500 mb-2">{g}</p>
              <div className="flex flex-wrap gap-2">
                {perms.map((p) => {
                  const on = (f.permissions || []).includes(p);
                  return (
                    <button type="button" key={p} onClick={() => togglePerm(p)} className={`text-xs px-3 py-1.5 rounded-full font-mono ${on ? 'bg-[#0F0F10] text-white' : 'bg-stone-100 text-stone-700 hover:bg-stone-200'}`}>{p}</button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
      <button type="submit" disabled={busy} className="btn-pill">{busy ? <Loader2 className="animate-spin" size={16} /> : <span>Save</span>}</button>
    </form>
  );
}

export function AdminContacts() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(null);
  const load = () => api.get('/admin/contacts').then((r) => setList(r.data || []));
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => { await api.patch(`/admin/contacts/${id}?status=${status}`); load(); };
  const remove = async (id) => { if (!window.confirm('Delete this submission?')) return; await api.delete(`/admin/contacts/${id}`); setOpen(null); load(); };
  return (
    <div>
      <AdminPageHeader title="Contact submissions" description="Messages sent through the public contact form." />
      <div className="rounded-3xl bg-white border border-black/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-xs uppercase tracking-widest text-stone-500"><tr><th className="text-left px-5 py-3">Date</th><th className="text-left px-5 py-3">Name</th><th className="text-left px-5 py-3">Email</th><th className="text-left px-5 py-3">Subject</th><th className="text-left px-5 py-3">Status</th><th></th></tr></thead>
          <tbody>
            {list.map((c) => (
              <tr key={c.id} className="border-t border-black/5 hover:bg-stone-50 cursor-pointer" onClick={() => setOpen(c)}>
                <td className="px-5 py-3 text-stone-600">{new Date(c.created_at).toLocaleDateString()}</td>
                <td className="px-5 py-3 font-medium">{c.name}</td>
                <td className="px-5 py-3">{c.email}</td>
                <td className="px-5 py-3">{c.subject || '—'}</td>
                <td className="px-5 py-3"><span className={`text-xs px-2 py-0.5 rounded-full ${c.status === 'new' ? 'bg-[#F4E94B]' : c.status === 'read' ? 'bg-stone-200' : 'bg-stone-100 text-stone-500'}`}>{c.status}</span></td>
                <td className="px-5 py-3 text-right"><button onClick={(e) => { e.stopPropagation(); remove(c.id); }} className="p-2 hover:text-red-600"><Trash2 size={14} /></button></td>
              </tr>
            ))}
            {!list.length && <tr><td colSpan={6} className="text-center py-12 text-stone-500">No submissions yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {open && <Modal onClose={() => setOpen(null)} title={open.subject || 'Contact submission'}>
        <div className="space-y-3 text-sm">
          <KV k="From" v={`${open.name} <${open.email}>`} />
          <KV k="Company" v={open.company || '—'} />
          <KV k="Phone" v={open.phone || '—'} />
          <KV k="Country" v={open.country || '—'} />
          <KV k="Language" v={open.lang} />
          <KV k="Date" v={new Date(open.created_at).toLocaleString()} />
          <div>
            <p className="text-stone-500 text-xs uppercase tracking-widest font-semibold mb-1">Message</p>
            <p className="whitespace-pre-wrap leading-relaxed">{open.message}</p>
          </div>
          <div className="flex flex-wrap gap-2 pt-2">
            {['new','read','archived'].map(s => (
              <button key={s} onClick={() => setStatus(open.id, s)} className={`text-xs px-3 py-1.5 rounded-full ${open.status === s ? 'bg-[#0F0F10] text-white' : 'bg-stone-100 hover:bg-stone-200'}`}>Mark as {s}</button>
            ))}
            <a href={`mailto:${open.email}`} className="text-xs px-3 py-1.5 rounded-full bg-[#F4E94B] hover:bg-[#FFEB3B]">Reply by email</a>
          </div>
        </div>
      </Modal>}
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div className="flex gap-3">
      <span className="text-stone-500 text-xs uppercase tracking-widest font-semibold w-24 mt-0.5">{k}</span>
      <span className="flex-1">{v}</span>
    </div>
  );
}
